import React, { useMemo, useState } from "react";
import { ChevronDown, Inbox } from "lucide-react";
import type { AttendanceRecord, Karyawan } from "../../controller/mobile/types";
import { fullDateLabel, monthKey, monthLabel, timeLabel } from "../../controller/mobile/Dateutils";
import { PENYETUJUAN_META, STATUS_META } from "../../controller/mobile/Statusmeta";

interface HistoryPageProps {
  employee: Karyawan;
  records: AttendanceRecord[];
}

interface MonthGroup {
  key: string; // "YYYY-MM"
  label: string; // "Juli 2026"
  records: AttendanceRecord[];
}

export default function HistoryPage({
  employee,
  records,
}: HistoryPageProps): React.ReactElement {
  const groups = useMemo<MonthGroup[]>(() => {
    const sorted = records
      .filter((r) => r.id_karyawan === employee.id)
      .sort((a, b) => (a.tanggal < b.tanggal ? 1 : -1));

    const map = new Map<string, MonthGroup>();
    for (const record of sorted) {
      const key = monthKey(record.tanggal);
      const existing = map.get(key);
      if (existing) {
        existing.records.push(record);
      } else {
        map.set(key, {
          key,
          label: monthLabel(record.tanggal),
          records: [record],
        });
      }
    }
    return Array.from(map.values());
  }, [records, employee.id]);

  // Hanya bulan paling baru yang terbuka secara default.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    groups.length > 0 ? { [groups[0].key]: true } : {}
  );

  const toggleMonth = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
        <h1 className="text-lg font-semibold text-slate-900">
          Riwayat Absensi
        </h1>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-5">
        {groups.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-400">
            <Inbox className="h-8 w-8" />
            <p className="text-sm">Belum ada riwayat absensi.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const isOpen = !!expanded[group.key];

              return (
                <section
                  key={group.key}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                >
                  <button
                    type="button"
                    onClick={() => toggleMonth(group.key)}
                    className="flex w-full items-center justify-between px-4 py-3.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">
                        {group.label}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                        {group.records.length} hari
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="space-y-2 border-t border-slate-100 px-4 pb-4 pt-3">
                      {group.records.map((record) => {
                        const statusMeta = record.status
                          ? STATUS_META[record.status]
                          : null;
                        const penyetujuanMeta =
                          record.tipe_aktivitas && record.tipe_aktivitas !== "none"
                            ? PENYETUJUAN_META[record.penyetujuan_aktivitas ?? "none"]
                            : null;

                        return (
                          <div
                            key={record.id}
                            className="rounded-xl border border-slate-100 bg-slate-50 p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-xs font-medium text-slate-600">
                                {fullDateLabel(record.tanggal)}
                              </p>
                              {statusMeta ? (
                                <span
                                  className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusMeta.className}`}
                                >
                                  {statusMeta.label}
                                </span>
                              ) : record.isPending ? (
                                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                  Sinkronisasi...
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2.5 grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-[11px] text-slate-400">Masuk</p>
                                <p className="font-mono text-sm text-slate-700">
                                  {record.jam_masuk ? timeLabel(record.jam_masuk) : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] text-slate-400">Pulang</p>
                                <p className="font-mono text-sm text-slate-700">
                                  {record.jam_keluar
                                    ? timeLabel(record.jam_keluar)
                                    : "-"}
                                </p>
                              </div>
                            </div>

                            {record.tipe_aktivitas &&
                              record.tipe_aktivitas !== "none" && (
                                <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-slate-200 pt-2.5 text-xs">
                                  <span className="rounded-full bg-white px-2 py-0.5 font-medium text-slate-600">
                                    {record.tipe_aktivitas === "koreksi_absen"
                                      ? "Koreksi absen"
                                      : record.tipe_aktivitas === "lembur"
                                      ? "Lembur"
                                      : "Lainnya"}
                                  </span>
                                  {penyetujuanMeta?.label && (
                                    <span
                                      className={`rounded-full px-2 py-0.5 font-medium ${penyetujuanMeta.className}`}
                                    >
                                      {penyetujuanMeta.label}
                                    </span>
                                  )}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}